import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
    Activity, CreditCard, ChefHat, CheckCircle, ShoppingCart, 
    Flame, Clock, AlertTriangle
} from 'lucide-react';
import { cn } from '../lib/utils';

// Format time ago
const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    try {
        const date = new Date(isoString);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        
        if (diff < 60) return "À l'instant";
        if (diff < 3600) return `${Math.floor(diff / 60)}min`;
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } catch {
        return '';
    }
};

const ActivityTicker = ({ className }) => {
    const { api, user } = useAuth();
    const [activities, setActivities] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const tickerRef = useRef(null);

    // Only show for managers
    const allowedRoles = ['ADMIN', 'DIRECTEUR', 'DIRECTEUR_ADJOINT', 'CHEF_CUISINE'];
    const canView = allowedRoles.includes(user?.role);

    // Fetch live feed
    const fetchActivities = useCallback(async () => {
        if (!canView) return;
        try {
            const res = await api().get('/pos/admin/dashboard/live-feed?filter_type=all&limit=10');
            setActivities(res.data?.activities || []);
        } catch (error) {
            console.error('Error fetching activities:', error);
        }
    }, [api, canView]);

    // Initial fetch and interval
    useEffect(() => {
        if (!canView) return;
        fetchActivities();
        const interval = setInterval(fetchActivities, 15000); // Refresh every 15s
        return () => clearInterval(interval);
    }, [fetchActivities, canView]);

    // Cycle through activities
    useEffect(() => {
        if (!canView || activities.length === 0 || isPaused) return;
        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % activities.length);
        }, 4000); // Show each activity for 4 seconds
        return () => clearInterval(interval);
    }, [activities, isPaused, canView]);

    if (!canView || activities.length === 0) {
        return null;
    }

    const current = activities[currentIndex];
    if (!current) return null;

    // Activity icon mapping
    const getIcon = (type) => {
        switch (type) {
            case 'payment': return <CreditCard className="w-3.5 h-3.5" />;
            case 'ready': return <CheckCircle className="w-3.5 h-3.5" />;
            case 'cooking': return <Flame className="w-3.5 h-3.5" />;
            case 'kitchen': return <ChefHat className="w-3.5 h-3.5" />;
            case 'new_order': return <ShoppingCart className="w-3.5 h-3.5" />;
            default: return <Activity className="w-3.5 h-3.5" />;
        }
    };

    // Color mapping
    const getColor = (color) => {
        const colors = {
            green: 'text-green-500 bg-green-500/10',
            emerald: 'text-emerald-500 bg-emerald-500/10',
            orange: 'text-orange-500 bg-orange-500/10',
            blue: 'text-blue-500 bg-blue-500/10',
            violet: 'text-violet-500 bg-violet-500/10',
            amber: 'text-amber-500 bg-amber-500/10',
            red: 'text-red-500 bg-red-500/10'
        };
        return colors[color] || 'text-gray-500 bg-gray-500/10';
    };

    return (
        <div 
            ref={tickerRef}
            className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 cursor-pointer",
                "hover:bg-muted transition-colors",
                "max-w-[280px] sm:max-w-[350px] lg:max-w-[400px]",
                className
            )}
            onClick={() => setIsPaused(!isPaused)}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            data-testid="activity-ticker"
            title={isPaused ? "Cliquer pour reprendre" : "Activité en direct"}
        >
            {/* Live indicator */}
            <div className="flex items-center gap-1.5">
                <span className={cn(
                    "w-2 h-2 rounded-full",
                    isPaused ? "bg-yellow-500" : "bg-green-500 animate-pulse"
                )} />
                <Activity className="w-3.5 h-3.5 text-muted-foreground" />
            </div>

            {/* Activity content */}
            <div className="flex items-center gap-2 flex-1 min-w-0 overflow-hidden">
                <span className={cn("p-1 rounded", getColor(current.color))}>
                    {getIcon(current.type)}
                </span>
                <span className="text-xs font-medium truncate">
                    {current.message}
                </span>
                <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    {formatTimeAgo(current.time)}
                </span>
            </div>

            {/* Counter */}
            <span className="text-[10px] text-muted-foreground">
                {currentIndex + 1}/{activities.length}
            </span>
        </div>
    );
};

export default ActivityTicker;
